import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { PlayGlyph } from '../nook/parts';
import { useVault } from '../session/useVault';
import { audioBeside, mediaType, stemOf } from '../vault/media';
import './doc.css';

type Fetch = { status: 'idle' } | { status: 'fetching' } | { status: 'failed' } | { status: 'ready'; url: string };

/**
 * A recording's audio from the vault, once it has reached it: a play button
 * that fetches the file when pressed, then the browser's own player. Until
 * then, [fallback] — the control that says the audio is still on the phone.
 */
export function VaultAudio({ notePath, size, fallback }: { notePath: string; size: number; fallback: ReactNode }) {
  const { state } = useVault();
  const vault = state.status === 'ready' ? state.vault : null;
  const audioPath = vault ? audioBeside(vault.listPaths(stemOf(notePath)), notePath) : null;
  const [fetched, setFetched] = useState<Fetch>({ status: 'idle' });

  useEffect(() => setFetched({ status: 'idle' }), [audioPath]);
  useEffect(() => {
    if (fetched.status !== 'ready') return undefined;
    return () => URL.revokeObjectURL(fetched.url);
  }, [fetched]);

  if (!vault || !audioPath) return <>{fallback}</>;
  if (fetched.status === 'ready') {
    return <audio className="player-audio" controls autoPlay src={fetched.url} />;
  }

  const play = async () => {
    setFetched({ status: 'fetching' });
    const bytes = await vault.readBytes(audioPath).catch(() => null);
    if (!bytes) {
      setFetched({ status: 'failed' });
      return;
    }
    const blob = new Blob([bytes as BlobPart], { type: mediaType(audioPath) ?? 'audio/mp4' });
    setFetched({ status: 'ready', url: URL.createObjectURL(blob) });
  };

  return (
    <div className="player">
      <button
        type="button"
        className="player-btn player-btn-live"
        style={{ width: size, height: size }}
        aria-label="Play"
        disabled={fetched.status === 'fetching'}
        onClick={play}
      >
        <PlayGlyph />
      </button>
      {fetched.status === 'fetching' && <span className="player-note player-note-said">Fetching…</span>}
      {fetched.status === 'failed' && <span className="player-note player-note-said">The audio could not be fetched</span>}
    </div>
  );
}
